import { splitSignature } from '@ethersproject/bytes'
import { MaxUint256 } from '@ethersproject/constants'
import { pair as pairABI } from '@jdiyield/abi'
import { chainData, ChainID } from '@jdiyield/constants'
import { AbiItem, AnotherToken, LPToken } from '@jdiyield/interfaces'
import { useWeb3React } from '@web3-react/core'
import BigNumber from 'bignumber.js'
import { useCallback, useMemo, useState } from 'react'
import Web3 from 'web3'

import { tokenAbi } from './allowance'
import { splitBalanceLP } from './calculate'
import getNodeUrl from './rpcRandom'
import { calculateGasMargin } from './utils'
import { web3Contract } from './web3'

type SignatureDataProps = {
  account: string
  chainId: number
  routerAddress: string
  lpToken: LPToken
}

export enum ApprovalState {
  UNKNOWN,
  NOT_APPROVED,
  PENDING,
  APPROVED
}

export enum Field {
  LIQUIDITY_PERCENT = 'LIQUIDITY_PERCENT',
  LIQUIDITY = 'LIQUIDITY',
  TOKEN_A = 'TOKEN_A',
  TOKEN_B = 'TOKEN_B'
}

export type SignatureDataResponse = {
  v: number
  r: string
  s: string
  deadline: number
}

export const DEFAULT_DEADLINE_FROM_NOW = 60 * 20
export const INITIAL_ALLOWED_SLIPPAGE = 80

export const getSignatureData = async ({
  account,
  chainId,
  routerAddress,
  lpToken
}: SignatureDataProps): Promise<SignatureDataResponse | null> => {
  const pairContract = await web3Contract(pairABI, lpToken.token.address)
  const library = (window as any).ethereum

  if (!pairContract || !library) throw new Error('missing dependencies')

  const result = await pairContract.methods.nonces(account).call()
  const nonce = `0x${result.toString(16)}`
  // const nonce = `0x00`

  return new Promise((res, rej) => {
    try {
      const deadlineForSignature: number = Math.ceil(Date.now() / 1000) + DEFAULT_DEADLINE_FROM_NOW
      const EIP712Domain = [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' }
      ]
      const domain = {
        name: 'Pancake LPs',
        version: '1',
        chainId,
        verifyingContract: lpToken.token.address
      }
      const Permit = [
        { name: 'owner', type: 'address' },
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'nonce', type: 'uint256' },
        { name: 'deadline', type: 'uint256' }
      ]

      const message = {
        owner: account,
        spender: routerAddress,
        value: lpToken.balance.toString(), // user input liquidity amount
        nonce,
        deadline: deadlineForSignature
      }

      const data = JSON.stringify({
        types: {
          EIP712Domain,
          Permit
        },
        domain,
        primaryType: 'Permit',
        message
      })

      library
        .send('eth_signTypedData_v4', [account, data])
        .then((signature) => splitSignature(signature.result))
        .then((signature) => {
          res({
            ...signature,
            deadline: deadlineForSignature
          })
        })
        .catch((e) => {
          if (e?.code !== 4001) {
            rej(new Error('Cannot sign'))
          }
        })
    } catch (e) {
      rej(e)
    }
  })
}

export function useApproveCallback({
  amountToApprove,
  token,
  spender
}: {
  amountToApprove: BigNumber
  token?: AnotherToken
  spender?: string
}): [ApprovalState, () => Promise<void>, () => Promise<void>] {
  const [currentAllowance, setCurrentAllowance] = useState(0)
  const [pendingApproval, setPendingApproval] = useState(false)
  const [tokenContract, setTokenContract] = useState(null)
  const { account, chainId, library } = useWeb3React()

  useMemo(() => {
    async function setContract() {
      const web3 = library
      const contract = new web3.eth.Contract(tokenAbi as any, token.address)

      setTokenContract(contract)
    }

    setContract()
  }, [library, token.address])

  const refetchAllowance = useCallback(async () => {
    const allowance = await tokenContract.methods.allowance(account, spender).call()
    setCurrentAllowance(allowance)
  }, [account, spender, tokenContract])

  // check the current approval status
  const approvalState: ApprovalState = useMemo(() => {
    if (!amountToApprove || !spender) return ApprovalState.UNKNOWN

    if (token.name.toUpperCase() === chainData[chainId]?.nativeCurrency?.name) return ApprovalState.APPROVED
    if (!currentAllowance) return ApprovalState.UNKNOWN

    // amountToApprove will be defined if currentAllowance is
    return new BigNumber(currentAllowance).isLessThan(amountToApprove)
      ? pendingApproval
        ? ApprovalState.PENDING
        : ApprovalState.NOT_APPROVED
      : ApprovalState.APPROVED
  }, [amountToApprove, currentAllowance, pendingApproval, spender, chainId, token.name])

  const approveCallback = useCallback(async (): Promise<void> => {
    setPendingApproval(true)
    if (approvalState !== ApprovalState.NOT_APPROVED) {
      console.error('approve was called unnecessarily')
      return
    }

    if (!token) {
      console.error('no token')
      return
    }

    if (!tokenContract) {
      console.error('tokenContract is null')
      return
    }

    if (!amountToApprove) {
      console.error('missing amount to approve')
      return
    }

    if (!spender) {
      console.error('no spender')
      return
    }

    let useExact = false
    const estimatedGas = await tokenContract.methods
      .approve(spender, MaxUint256)
      .estimateGas()
      .catch(() => {
        // general fallback for tokens who restrict approval amounts
        useExact = true
        return tokenContract.methods.approve(spender, amountToApprove.toString()).estimateGas()
      })

    return tokenContract.methods
      .approve(spender, useExact ? amountToApprove.integerValue().toString() : MaxUint256)
      .send({ gas: calculateGasMargin(estimatedGas), from: account })
      .then((response) => {
        console.log('Success Approve!', response)
      })
      .catch((error: Error) => {
        console.error('Failed to approve token', error)
        throw error
      })
      .finally(() => {
        setPendingApproval(false)
      })
  }, [approvalState, token, amountToApprove, spender, account, tokenContract])

  return [approvalState, approveCallback, refetchAllowance]
}

export function useFetchBalance({ lpAddress }: { lpAddress?: string }) {
  const { chainId, account } = useWeb3React()
  const [lpData, setLPData] = useState({
    tokenA: {} as any,
    tokenB: {} as any,
    totalSupply: '',
    balance: '',
    amountTokenAPerTokenB: new BigNumber(0),
    amountTokenBPerTokenA: new BigNumber(0)
  })

  const refetchBalance = useCallback(
    async (currentChainId?: ChainID) => {
      const RPC_URL = getNodeUrl(currentChainId || (chainId as ChainID))
      const web3 = await new Web3(RPC_URL)
      const pairContract = new web3.eth.Contract(pairABI as AbiItem | AbiItem[], lpAddress)
      const balanceOfLP = await pairContract.methods.balanceOf(account).call()
      const { token0, token1, totalSupply } = await splitBalanceLP(web3, { lpAddress, balance: balanceOfLP })

      setLPData({
        totalSupply,
        tokenA: token0,
        tokenB: token1,
        balance: balanceOfLP,
        amountTokenAPerTokenB: token1.amount.dividedBy(token0.amount),
        amountTokenBPerTokenA: token0.amount.dividedBy(token1.amount)
      })
    },
    [account, chainId, lpAddress]
  )

  return { lpData, refetchBalance }
}

export function calculateSlippageAmount(value: BigNumber, slippage: number): string[] {
  if (slippage < 0 || slippage > 10000) {
    throw Error(`Unexpected slippage value: ${slippage}`)
  }
  slippage = 10000 // Noted: just fixed slippage to 10000, should slippage ui for user
  const excludeSlipage = new BigNumber(10000 - slippage)
  const includeSlipage = new BigNumber(10000 + slippage)
  const BIG_HUNDRED = new BigNumber(10000)

  return [
    value.times(excludeSlipage).dividedBy(BIG_HUNDRED).integerValue().toString(),
    value.times(includeSlipage).dividedBy(BIG_HUNDRED).integerValue().toString()
  ]
}
